import React, { useState, useEffect } from 'react';
import { Button, Stack, TextField } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MDialog from '../../../components/controls/MDialog';
import { CloneRuleDialogProps } from '../types';

/**
 * Dialog component that prompts the user for a new rule name when cloning a rule.
 *
 * Confirming this dialog does NOT persist anything — the caller is responsible for
 * creating a local draft and letting the user save when ready.
 *
 * @param {object}   props
 * @param {boolean}  props.open        - Whether the dialog is visible.
 * @param {string}   props.sourceName  - Name of the rule being cloned (used as placeholder hint).
 * @param {Function} props.onClose     - Callback invoked when the dialog is dismissed without cloning.
 * @param {Function} props.onConfirm   - Callback invoked with the trimmed new name when the user confirms.
 * @returns {React.ReactElement}
 */
const CloneRuleDialog: React.FC<CloneRuleDialogProps> = ({ open, sourceName, onClose, onConfirm }) => {
    const [cloneName, setCloneName] = useState('');
    const [touched, setTouched] = useState(false);

    // Reset input state every time the dialog opens so stale values are not carried over.
    useEffect(() => {
        if (open) {
            setCloneName('');
            setTouched(false);
        }
    }, [open]);

    const trimmedName = cloneName.trim();
    const isNameEmpty = trimmedName === '';

    const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setCloneName(ev.target.value);
        setTouched(true);
    };

    const handleConfirm = () => {
        if (isNameEmpty) return;
        onConfirm(trimmedName);
    };

    return (
        <MDialog
            maxWidth="sm"
            open={open}
            handleClose={onClose}
            title="Clone Rule"
            name="clone-rule-dialog"
            action={
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                        startIcon={<ContentCopyIcon />}
                        color="info"
                        variant="contained"
                        disabled={isNameEmpty}
                        onClick={handleConfirm}
                    >
                        Clone
                    </Button>
                </Stack>
            }
        >
            <TextField
                autoFocus
                fullWidth
                required
                label="New Rule Name"
                value={cloneName}
                onChange={handleChange}
                error={touched && isNameEmpty}
                helperText={touched && isNameEmpty ? 'Rule name cannot be empty' : `Cloning from: "${sourceName}"`}
                placeholder="Enter a unique name for the cloned rule..."
                variant="outlined"
            />
        </MDialog>
    );
};

export default React.memo(CloneRuleDialog);
